import React from 'react';
import classnames from 'classnames';
import { noop, find, get, isEmpty } from 'lodash';

// local dependencies
import { searchInput as SearchInput } from 'ui/views/generic/searchInput';
import { table as Table } from 'modules/table';
import { getLabel } from 'utils/getLabel';
import { OutsideClickHandler } from 'vendors/OutsideClickHandler';
import { isVisible } from 'resolvers/getVisibleEntities';

/**
 * @type {object}
 * @desc A React presentation/dumb view component.
 * @example
 * import { tableSearchFlyoutWidget as TableSearchFlyoutWidget } from 'ui/views/widgets/tableSearchFlyoutWidget';
 *
 * // render
 * <TableSearchFlyoutWidget propName='propValue'/>
 */
export const tableSearchFlyoutWidget = ( props ) => {

    // get search filter state and config
    const searchFilter = find( props.filters, { name: 'search' } );
    const infoFilter = get( searchFilter, 'config.info', {} );
    const searchQuery = get( searchFilter, 'value', '' );

    // ui configurations
    const borderlessSearch = get( props.layoutConfig, 'styles.borderlessSearch', true );
    const inlineTableView = get( props.layoutConfig, 'styles.inlineTableView', false );
    const expandedTableView = get( props.layoutConfig, 'styles.expandedTableView', false );
    const searchPattern = get( props.layoutConfig, 'inputConfig.searchPattern', null );

    // dynamic widget class name
    const widgetClassName = classnames( 'ui-table-search-flyout-widget', props.className );

    // table container class name
    const tableContainerClassName = classnames( 'ui-table-search-flyout-widget__table-container', {
        'ui-table-search-flyout-widget__table-container--inline': inlineTableView,
        'ui-table-search-flyout-widget__table-container--expanded': expandedTableView,
    } );

    const focus = () => {
        if( ! isEmpty( infoFilter ) && isVisible( infoFilter, props.visibility ) ) {
            return (
                <div className='ui-table-search-flyout-widget__custom-tooltip'
                    dangerouslySetInnerHTML={ { __html: `<ul style='list-style: disc'>${ getLabel( infoFilter.message ) }</ul>` } } >
              </div>
            );
        }
    };

    return (
        <OutsideClickHandler className={ widgetClassName } disabled={ inlineTableView } onOutsideClick={ () => props.onFilterChange( { name: 'search', value: '' } ) }>
            {/* search input */}
            {
                searchFilter ? (
                    <SearchInput
                    className='ui-table-search-flyout-widget__search'
                    loading={ props.isFetching && 'filters' === props.fetchingType }
                    value={ searchQuery }
                    placeholder={ getLabel( searchFilter.placeholder ) }
                    onChange={ ( value ) => props.onFilterChange( { name: 'search', value } ) }
                    searchPattern={ searchPattern }
                    info= { focus() }
                    activateTooltip= { isVisible( infoFilter, props.visibility ) }
                    borderless={ borderlessSearch }
                />
                ) : null
            }

            {/* table */}
            {
                ( props.disableDataView || isEmpty( searchQuery ) || ( props.isInitialData && 0 === props.items.length ) ) ? null : (
                    <div className={ tableContainerClassName }>
                        <Table
                            visibility={ props.visibility }
                            layoutConfig={ props.layoutConfig }
                            isFetching={ props.isFetching }
                            fetchingType={ props.fetchingType }
                            disablePagination={ props.disablePagination }

                            // data
                            searchQuery={ searchQuery }
                            items={ props.items }
                            columns={ props.columns }
                            sortAndFilter={ props.sortAndFilter }
                            pagination={ props.pagination }
                            searchableColumns={ props.searchableColumns }
                            actionsConfig={ props.actionsConfig }
                            isInitialData={ props.isInitialData }

                            // methods
                            onSortAndFilter={ props.onSortAndFilter }
                            onPagination={ props.onPagination }
                            onActionClick={ props.onActionClick }
                        />
                    </div>
                )
            }
        </OutsideClickHandler>
    );
};

// set display name
tableSearchFlyoutWidget.displayName = 'tableSearchFlyoutWidget';

// set default props
tableSearchFlyoutWidget.defaultProps = {
    isInitialData: true,
    isFetching: false,
    fetchingType: null,
    disablePagination: false,
    disableDataView: false,

    // static data
    visibility: {},
    widgetConfig: {},
    layoutConfig: {},
    searchableColumns: [],
    actionsConfig: {},

    // data
    items: [],
    columns: {
        primary: [],
        secondary: [],
    },
    filters: [],
    sortAndFilter: {},
    pagination: {},

    // methods
    onFilterChange: noop,
    onSortAndFilter: noop,
    onPagination: noop,
    onActionClick: noop,
    onDataVisibilityChange: noop,
};

// prop types
tableSearchFlyoutWidget.propTypes = {};
